import {PlaybackControl, TrackDTO} from '../types';
import {QueueManager} from '../playback/QueueManager';

export abstract class PlayerStrategy {
    constructor(
        protected readonly queueManager: QueueManager,
        protected readonly control: PlaybackControl,
    ) {}

    get track(): TrackDTO | undefined {
        return this.queueManager.current;
    }

    get queue(): TrackDTO[] {
        return this.queueManager.list;
    }

    onChangeQueue(): TrackDTO[] {
        return this.queueManager.list;
    }

    abstract execute(track: TrackDTO, queue: TrackDTO[]): void;
    abstract onTrackEnd(): void;
}

export class SimplePlayerStrategy extends PlayerStrategy {
    execute(track: TrackDTO, queue: TrackDTO[]): void {
        this.queueManager.replace(track, queue);
    }

    onTrackEnd(): void {
        const isLastTrack = this.queueManager.currentIndex >= this.queueManager.size - 1;

        if (isLastTrack) {
            this.control.stop();
            return;
        }

        this.control.next();
    }
}

export class ShufflePlayerStrategy extends PlayerStrategy {
    private originalOrder: TrackDTO[] = [];

    execute(track: TrackDTO, queue: TrackDTO[]): void {
        this.originalOrder = [...queue];

        const shuffled = this.shuffleKeepingTrackPosition([...queue], track);
        this.queueManager.replace(track, shuffled);
    }

    override onChangeQueue(): TrackDTO[] {
        return [...this.originalOrder];
    }

    onTrackEnd(): void {
        const isLastTrack = this.queueManager.currentIndex >= this.queueManager.size - 1;

        if (isLastTrack) {
            this.control.stop();
            return;
        }

        this.control.next();
    }

    private shuffleKeepingTrackPosition(queue: TrackDTO[], track: TrackDTO): TrackDTO[] {
        const originalIndex = queue.findIndex(t => t.id === track.id);

        for (let i = 0; i < queue.length; i++) {
            const j = Math.floor(Math.random() * queue.length);
            [queue[i], queue[j]] = [queue[j], queue[i]];
        }

        const shuffledIndex = queue.findIndex(t => t.id === track.id);

        if (originalIndex >= 0 && shuffledIndex >= 0 && originalIndex !== shuffledIndex) {
            [queue[originalIndex], queue[shuffledIndex]] =
                [queue[shuffledIndex], queue[originalIndex]];
        }

        return queue;
    }
}

export abstract class StrategyLoopDecorator extends PlayerStrategy {
    private innerStrategy: PlayerStrategy;

    constructor(innerStrategy: PlayerStrategy, queueManager: QueueManager, control: PlaybackControl) {
        super(queueManager, control);
        this.innerStrategy = innerStrategy;
    }

    get wrapped(): PlayerStrategy {
        return this.innerStrategy;
    }

    set wrapped(next: PlayerStrategy) {
        const currentTrack = this.track;
        const view = this.innerStrategy.onChangeQueue();

        this.innerStrategy = next;

        if (currentTrack) {
            next.execute(currentTrack, view);
        }
    }

    get track(): TrackDTO | undefined {
        return this.queueManager.current;
    }

    execute(track: TrackDTO, queue: TrackDTO[]): void {
        this.innerStrategy.execute(track, queue);
    }
}

export class TrackLoopDecorator extends StrategyLoopDecorator {
    onTrackEnd(): void {
        this.control.stop();
        this.control.play();
    }
}

export class PlaylistLoopDecorator extends StrategyLoopDecorator {
    onTrackEnd(): void {
        this.control.next();
    }
}

export class NoneLoopDecorator extends StrategyLoopDecorator {
    onTrackEnd(): void {
        this.wrapped.onTrackEnd();
    }
}
